import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';

import { InsuranceClaimList } from '@models/InsuranceClaim';
import { tInsuranceTypeEnum } from '@models/InsuranceType';
import DataTable, { TableColumn } from 'react-data-table-component';
import { ClaimBadge } from '@components/Base';
import { formatDateTime } from '@utils/datetime';
import { AmountWithDiscount } from '@components/Base';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

import { routeUserDashboardInsuranceClaimDetailsPage } from '../route';
import { claimsFetch, ClaimsFetchPayload } from './action';
import { selectInsuranceClaimListPageReducer } from './selector';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const ViewDetailsButton = ({ claimableType, claimableId }: InsuranceClaimList) => {
  return (
    <Button
      as={Link}
      to={routeUserDashboardInsuranceClaimDetailsPage({ claimableType, claimableId })}
      variant="outline-info"
      size="sm"
    >
      <FontAwesomeIcon icon={faEye} />
    </Button>
  );
};

const ClaimListView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams,] = useSearchParams();
  const { claims, isLoading, pagination } = useSelector(selectInsuranceClaimListPageReducer);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const columns: TableColumn<InsuranceClaimList>[] = [
    {
      cell: (row) => <ViewDetailsButton {...row} />,
      button: true,
      center: true,
      width: '120px',
    },
    {
      name: t('common:billingType'),
      selector: ({ billingTypeCode }) => billingTypeCode,
      width: '120px',
    },
    {
      name: t('common:policyNumber'),
      selector: ({ policyNumber }) => policyNumber,
      width: '180px',
    },
    {
      name: t('common:claimNumber'),
      selector: ({ claimNumber }) => claimNumber,
      width: '180px',
    },
    {
      name: "Claim Amount",
      selector: ({ totalClaimedAmount }) => <AmountWithDiscount skipIncluded finalAmount={totalClaimedAmount} />,
    },
    {
      name: t('common:insuranceType'),
      selector: ({ insuranceType }) => tInsuranceTypeEnum(insuranceType),
      width: '220px',
    },
    {
      name: t('insuranceClaimDetails.registerDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      width: '200px',
    },
    {
      name: t('common:status'),
      cell: ({ status }) => <ClaimBadge status={status} />,
    },
    {
      name: t('common:memberName'),
      cell: ({ insurancePolicyUser }) => insurancePolicyUser?.name,
      width: '120px',
    },
    {
      name: t('common:creator'),
      cell: ({ creator }) => creator?.name,
      width: '120px',
    },
  ];

  useEffect(() => {
    const payload: ClaimsFetchPayload = { ...Object.fromEntries(searchParams), page, perPage };
    dispatch(claimsFetch(payload));
  }, [searchParams, page, perPage]);

  return (
    <DataTable
      columns={columns}
      data={claims ?? []}
      customStyles={customStyles}
      fixedHeader
      pagination
      paginationServer
      progressPending={isLoading}
      paginationPerPage={pagination?.perPage || 10}
      paginationTotalRows={pagination?.total ?? 0}
      onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
      onChangePage={(newPage) => setPage(newPage)}
    />
  );
};

export { ClaimListView };
